import type { ReactNode } from 'react';
import {
    ExclamationTriangleIcon,
    PauseIcon,
    PlayIcon,
    StopIcon
} from '@heroicons/react/24/outline';
import type { DmQuickstart } from '../../../../../services/dmQuickstarts';
import { AppointmentDateField } from '../../../../shared/AppointmentDateField';
import { SearchableSelect } from '../../../../shared/SearchableSelect';
import { ConfirmLayout } from './ConfirmLayout';
import { DmButton, DmErrorText, Field } from './DmControlPrimitives';
import { todayYmd, ymdInDays } from './dmControlUtils';

const QUICKSTART_TRIGGER =
    'h-9 rounded-md border border-gray-300 bg-white px-3 text-sm shadow-sm hover:border-gray-400 aria-expanded:border-indigo-500 aria-expanded:ring-2 aria-expanded:ring-indigo-500 transition-colors duration-200';

interface StepProps {
    busy: boolean;
    error: string | null;
    onCancel: () => void;
}

function CancelButton({ busy, onCancel }: Pick<StepProps, 'busy' | 'onCancel'>) {
    return (
        <DmButton onClick={onCancel} disabled={busy}>
            Cancel
        </DmButton>
    );
}

interface QuickstartsState {
    quickstarts: readonly DmQuickstart[];
    loading: boolean;
    loadError: string | null;
}

interface StartConfirmProps extends StepProps, QuickstartsState {
    selectedId: string | null;
    onSelect: (id: string) => void;
    onConfirm: () => void;
}

function quickstartPicker({
    quickstarts,
    loading,
    loadError,
    selectedId,
    onSelect
}: QuickstartsState & Pick<StartConfirmProps, 'selectedId' | 'onSelect'>): ReactNode {
    if (loading) return <p className="text-[13px] text-gray-500">Loading quickstarts…</p>;
    if (loadError) return <DmErrorText message={loadError} />;
    if (quickstarts.length === 0) {
        return (
            <p className="text-[13px] text-gray-500">
                No quickstarts found for this practice in Dental Monitoring.
            </p>
        );
    }
    const chosen = quickstarts.find((quickstart) => quickstart.id === selectedId);
    // No visible label: the status line above already says to pick one, and `label` names the trigger.
    return (
        <div>
            <SearchableSelect
                mode="single"
                label="Quickstart"
                placeholder="Choose a quickstart"
                searchPlaceholder="Search quickstarts"
                options={quickstarts.map((quickstart) => ({
                    value: quickstart.id,
                    label: quickstart.name,
                    sublabel: quickstart.description ?? undefined
                }))}
                value={selectedId}
                onChange={onSelect}
                triggerClassName={QUICKSTART_TRIGGER}
            />
            {/* Two lines kept whatever the description, so picking one doesn't move the buttons. */}
            <p
                className="mt-2 line-clamp-2 min-h-8 text-xs text-gray-500"
                title={chosen?.description ?? undefined}
            >
                {chosen?.description}
            </p>
        </div>
    );
}

export function StartConfirm(props: StartConfirmProps) {
    const { busy, error, onCancel, onConfirm, selectedId } = props;
    return (
        <ConfirmLayout
            error={error}
            cancel={<CancelButton busy={busy} onCancel={onCancel} />}
            confirm={
                <DmButton
                    kind="start"
                    icon={PlayIcon}
                    disabled={busy || !selectedId}
                    loading={busy}
                    onClick={onConfirm}
                >
                    {busy ? 'Starting…' : 'Start monitoring'}
                </DmButton>
            }
        >
            {quickstartPicker(props)}
        </ConfirmLayout>
    );
}

interface PauseConfirmProps extends StepProps {
    /** Already paused: the step moves the resume date instead. */
    paused: boolean;
    resumeOn: string;
    savedResumeOn: string | null;
    onResumeOnChange: (ymd: string) => void;
    onConfirm: () => void;
}

export function PauseConfirm({
    busy,
    error,
    onCancel,
    paused,
    resumeOn,
    savedResumeOn,
    onResumeOnChange,
    onConfirm
}: PauseConfirmProps) {
    const tooEarly = resumeOn < ymdInDays(1);
    return (
        <ConfirmLayout
            error={error}
            cancel={<CancelButton busy={busy} onCancel={onCancel} />}
            confirm={
                <DmButton
                    kind="warning"
                    icon={PauseIcon}
                    disabled={busy || tooEarly || (paused && resumeOn === savedResumeOn)}
                    loading={busy}
                    onClick={onConfirm}
                >
                    {busy ? 'Saving…' : paused ? 'Schedule resume' : 'Pause monitoring'}
                </DmButton>
            }
        >
            <Field label="Resume on" htmlFor="dm-resume-on">
                <div className="w-56 max-w-full">
                    <AppointmentDateField
                        id="dm-resume-on"
                        value={resumeOn}
                        onChange={onResumeOnChange}
                        todayIso={todayYmd()}
                        minDate={ymdInDays(1)}
                    />
                </div>
            </Field>
        </ConfirmLayout>
    );
}

interface StopConfirmProps extends StepProps {
    patientName?: string;
    onConfirm: () => void;
}

export function StopConfirm({ busy, error, onCancel, patientName, onConfirm }: StopConfirmProps) {
    return (
        <ConfirmLayout
            error={error}
            cancel={<CancelButton busy={busy} onCancel={onCancel} />}
            confirm={
                <DmButton
                    kind="danger"
                    icon={StopIcon}
                    disabled={busy}
                    loading={busy}
                    onClick={onConfirm}
                >
                    {busy ? 'Stopping…' : 'Stop monitoring'}
                </DmButton>
            }
        >
            <div className="flex items-start gap-2">
                <ExclamationTriangleIcon className="h-5 w-5 shrink-0 text-red-500" />
                <div className="space-y-1">
                    <p className="text-sm font-semibold text-gray-800">
                        This will stop {patientName ? `${patientName}'s` : "this patient's"}{' '}
                        monitoring.
                    </p>
                    <p className="text-xs text-gray-500">
                        All current settings are lost: restarting means choosing the plan, treatment
                        type and protocol again. Their monitoring history stays available in Dental
                        Monitoring.
                    </p>
                </div>
            </div>
        </ConfirmLayout>
    );
}
